/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import https from 'https';
import getConfig from '../config';
import { APP_TYPES } from '../containers/SearchResultsPage/SearchConstants';
import { getLogger } from '../utils/SearchLogger';
import SearchConfig from '../utils/SearchConfig';

class JumpService {
	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * Returns a jump url for the given type and url.
	 * NOTE: This list should be updated whenever new targets are added. Please also see JumpTargets.js
	 * NOTE: Tutorials/Learn/Forums only have 'Prod' URLs so we do not generate jump urls on environments other than Prod.
	 * NOTE: Whenever this list is updated, please inform CC Search clients so that they can get their IMS client IDs setup as needed.
	 * @param {string} type - the search category or app type.
	 * @param {string} url - the url
	 * @param {boolean} isShowAllUrl - indicates if the url is a Show All/View All link
	 */
	async getJumpURLforResult(type: string, url: string, isShowAllUrl: boolean) {
		let target: any = undefined;
		let jumpTargets: Record<string, any> | null = null;
		const searchConfig = SearchConfig.getSearchConfig();

		if (searchConfig) {
			jumpTargets = searchConfig.jumpTargets;
		}

		if (jumpTargets) {
			switch (type) {
				case APP_TYPES.APPS:
				case APP_TYPES.LEARN_ON_THE_WEB:
					target = jumpTargets.ccWeb;
					break;

				case APP_TYPES.STOCK:
				case APP_TYPES.TEMPLATES:
					target = jumpTargets.stock;
					break;

				case APP_TYPES.TUTORIALS:
				case APP_TYPES.LEARN:
					target = jumpTargets.helpx;
					break;

				case APP_TYPES.ADOBE_FORUM:
					// Since the URLs returned for Forums are only for Prod, we don't attempt jumps on other environments.
					if (this.searchHostContext.environment && this.searchHostContext.environment !== 'prod') {
						target = undefined;
					} else {
						target = jumpTargets.forums;
					}

					break;

				case APP_TYPES.LIGHTROOM:
					target = jumpTargets.lrWeb;
					break;

				case APP_TYPES.SPARK:
					target = jumpTargets.projectx;
					break;

				case APP_TYPES.SPARK_TEMPLATES:
					target = jumpTargets.projectx;
					break;

				case APP_TYPES.CLOUD_DOCS: {
					const URLJson = new URL(url);
					const acrobatURL = URLJson.hostname.includes('acrobat');
					if (acrobatURL) {
						if (this.searchHostContext.environment && this.searchHostContext.environment === 'prod') {
							target = jumpTargets[APP_TYPES.ACROBAT];
						} else {
							target = jumpTargets[APP_TYPES.ACROBAT_STAGE];
						}
					} else {
						target = jumpTargets.assetsWeb;
					}
					break;
				}
				case APP_TYPES.CREATIVE_CLOUD:
				case APP_TYPES.LIBRARIES:
				case APP_TYPES.MOBILE_CREATIONS:
					target = jumpTargets.assetsWeb;
					break;

				case APP_TYPES.XD_ARTIFACTS:
					target = jumpTargets.xdWeb;
					break;

				default:
					// eslint-disable-next-line no-prototype-builtins
					if (jumpTargets.hasOwnProperty(type)) {
						target = jumpTargets[type];
						this.logger.info('Search Component: JumpService', 'deafult matching of jump targets', type);
					} else {
						this.logger.info(
							'Search Component: JumpService',
							'Unrecognized result type in getJumpURLforResult handler',
							type,
						);
					}
			}
		} else {
			this.logger.error('Jump Service', 'No jumpTargets found in the server config.');
		}

		// Handle Show All/View All links - these may use a target other than the one defined for
		// this app's result urls.
		if (isShowAllUrl && target && target.viewAllJumpTarget) {
			target = jumpTargets && jumpTargets[target.viewAllJumpTarget];
		}

		let result = Promise.resolve(url);

		if (this.searchHostContext.imsInfo.accessToken && url && target) {
			result = this._performJumpURLRequest(url, target);
		}

		return result;
	}

	static instance: JumpService;
	logger: Logger;
	searchHostContext: HostContext;

	constructor(searchHostContext: HostContext) {
		if (!JumpService.instance) {
			this.searchHostContext = searchHostContext;
			this.logger = getLogger();
			JumpService.instance = this;
		}

		return JumpService.instance;
	}

	/**
	 * This method fetches the IMS Jump URL for the specified target. The method will resolve with a valid jump URL if successful and an error, if not.
	 * @param {string} targetURL - the target URL the user should eventually be taken to.
	 * @param {JumpTarget} target - the target's IMS details.
	 * @returns {Promise<String|Error>} -
	 */
	_performJumpURLRequest(targetURL: string, target: JumpTarget): Promise<string> {
		/* eslint-disable camelcase */
		const bodyData: Record<string, any> = {
			bearer_token: this.searchHostContext.imsInfo.accessToken,
			target_client_id: target.clientId,
			target_redirect_uri: targetURL,
			target_scope: target.scopes,
			target_response_type: target.responseType,
		};

		if (this.searchHostContext.hostInfo.imsClientId) {
			bodyData.client_id = this.searchHostContext.hostInfo.imsClientId;
		}

		/* eslint-enable camelcase */
		return new Promise((resolve, reject) => {
			// URI encode the post body options and convert to a string.
			const postData = this.encodeOptionsAsString(bodyData);
			const request = https.request(
				{
					hostname: getConfig(this.searchHostContext.environment, 'IMS'),
					path: '/ims/jumptoken/v1',
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
						Accept: 'application/json',
						authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
					},
					// TODO: TS These needs to enabled
					// dataType: 'json',
					// withCredentials: false,
				},
				(res: any) => {
					if (res.statusCode >= 300) {
						this.logger.error('Jump Service', 'Problem with Jump Token request', res);
						const jumpServiceError = new Error('Jump Service error');
						reject(jumpServiceError);
					} else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						// https://jira.corp.adobe.com/browse/CCH-4427
						res.setEncoding('utf8');
						res.on('data', (chunk) => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								const response = JSON.parse(data);

								if (response && response.jump) {
									resolve(response.jump);
								} else {
									const jumpServiceError = new Error(
										'Jump Service error - no jump URL in the response',
									);
									reject(jumpServiceError);
								}
							} catch (e) {
								this.logger.error(
									'Jump Service',
									'Problem with parsing Jump Token response',
									e.toString(),
								);
								const jumpServiceError = new Error('Jump Service parse error');
								reject(jumpServiceError);
							}
						});
					}
				},
			);
			request.on('error', (err) => {
				// There was a connection error of some sort
				this.logger.error('Jump Service', 'Problem with Jump Token request', err.toString());
				const jumpServiceError = new Error('Jump Service error');
				reject(jumpServiceError);
			});
			request.write(postData);
			request.end();
		});
	}

	/**
	 * Encodes the keys and values and composes a query string with the values.
	 * @param {Object} options
	 * @returns {string} URL encoded query string
	 */
	encodeOptionsAsString(options: Record<string, any>) {
		return Object.keys(options)
			.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(options[key])}`)
			.join('&');
	}
}

export default JumpService;
